/**
 * LYT Communications - Admin Project Dashboard
 * Version: 3.0.0
 * Updated: 2026-02-03
 * Route: #admin-projects
 * 
 * Multi-project dashboard with:
 * - Project selector (loads all projects from DB)
 * - Progress stats from real segment data
 * - Bulk assignment panel (select segments → assign contractor)
 * - Section breakdown with drill-down
 * - Open issues list
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Plus, Map, Users, AlertCircle, Upload, BarChart3, Loader,
  Sun, Moon, ChevronRight, Zap, AlertTriangle, UserPlus,
  ChevronDown, RefreshCw, DollarSign, Download, Edit3
} from 'lucide-react';
import { loadProjects, loadFullProject, loadFullProjectWithLineItems, loadIssues, loadLineItems, updateLineItemRate, loadChangeOrders, createChangeOrder, updateChangeOrderField } from '../services/mapService';
import ChangeOrderManager from '../components/Workflow/ChangeOrderManager';

function AdminProjectDashboard({ darkMode, setDarkMode, user, setCurrentPage, selectedProjectId, setSelectedProjectId }) {
  const [loading, setLoading] = useState(true);
  const [allProjects, setAllProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [segments, setSegments] = useState([]);
  const [splicePoints, setSplicePoints] = useState([]);
  const [issues, setIssues] = useState([]);
  const [dataSource, setDataSource] = useState('loading');

  const [showVersion, setShowVersion] = useState(false);

  // Billing tab state
  const [activeView, setActiveView] = useState('overview'); // 'overview' | 'billing'
  const [lineItems, setLineItems] = useState([]);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const [editingRate, setEditingRate] = useState(null); // line_item_id being edited
  const [editRateValue, setEditRateValue] = useState('');

  // Change orders state
  const [changeOrders, setChangeOrders] = useState([]);
  const [cosLoading, setCosLoading] = useState(false);

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const successGreen = '#28a745';
  const warningOrange = '#FF9800';

  const fetchProjectData = useCallback(async (projId) => {
    setLoading(true);
    try {
      const data = await loadFullProjectWithLineItems(projId);
      const issueData = await loadIssues(projId);
      setProject(data.project);
      setSegments(data.segments || []);
      setSplicePoints(data.splicePoints || []);
      setIssues(Array.isArray(issueData) ? issueData : []);
      setDataSource(data.isDemo ? 'demo' : 'live');
    } catch (err) {
      console.error('[AdminProjects] Load failed:', err);
      setDataSource('error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const projects = (await loadProjects()).filter(p => p && p.project_id && p.project_id !== 'OFFLINE');
        if (!cancelled) {
          setAllProjects(projects);
          const pid = selectedProjectId || (projects[0]?.project_id) || null;
          if (setSelectedProjectId && pid !== selectedProjectId) setSelectedProjectId(pid);
          await fetchProjectData(pid);
        }
      } catch (err) {
        console.error('[AdminProjects] Init failed:', err);
        if (!cancelled) { setLoading(false); setDataSource('error'); }
      }
    }
    init();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProjectChange = async (newId) => {
    if (setSelectedProjectId) setSelectedProjectId(newId);
    await fetchProjectData(newId);
  };

  const stats = useMemo(() => {
    if (!segments.length) return { boring: 0, pulling: 0, splicing: 0, totalFootage: 0, totalSegments: 0, totalSplices: 0, crews: 0, sections: [], unassignedBoring: 0, unassignedPulling: 0, billedPct: 0, paidPct: 0 };
    const total = segments.length;
    const boringDone = segments.filter(s => ['QC Approved', 'Complete'].includes(s.boring_status)).length;
    const pullingDone = segments.filter(s => ['QC Approved', 'Complete'].includes(s.pulling_status)).length;
    const splicingDone = splicePoints.filter(sp => ['QC Approved', 'Complete'].includes(sp.status)).length;
    const splicingTotal = splicePoints.length || 1;
    const totalFootage = segments.reduce((sum, s) => sum + (parseInt(s.footage) || 0), 0);
    const contractors = new Set();
    let unassignedBoring = 0;
    let unassignedPulling = 0;
    // Billing stats
    const billedCount = segments.filter(s => s.boring_billing_status && s.boring_billing_status !== 'Not Billed').length
      + segments.filter(s => s.pulling_billing_status && s.pulling_billing_status !== 'Not Billed').length;
    const billableTotal = (boringDone + pullingDone) || 1;
    const paidCount = segments.filter(s => s.boring_billing_status === 'Paid').length
      + segments.filter(s => s.pulling_billing_status === 'Paid').length;
    segments.forEach(seg => {
      if (seg.boring_assigned_to) contractors.add(seg.boring_assigned_to);
      else unassignedBoring++;
      if (seg.pulling_assigned_to) contractors.add(seg.pulling_assigned_to);
      else unassignedPulling++;
    });
    const sections = [...new Set(segments.map(s => s.section).filter(Boolean))].sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0));
    return {
      boring: Math.round((boringDone / total) * 100),
      pulling: Math.round((pullingDone / total) * 100),
      splicing: Math.round((splicingDone / splicingTotal) * 100),
      totalFootage, totalSegments: total, totalSplices: splicePoints.length,
      crews: contractors.size, sections, unassignedBoring, unassignedPulling,
      billedPct: Math.round((billedCount / billableTotal) * 100),
      paidPct: Math.round((paidCount / billableTotal) * 100),
    };
  }, [segments, splicePoints]);

  const openIssues = issues.filter(i => i.status !== 'Resolved' && i.status !== 'Closed');

  const loadBillingData = async () => {
    if (!selectedProjectId) return;
    setLineItemsLoading(true);
    try {
      const items = await loadLineItems(selectedProjectId);
      setLineItems(items);
    } catch (err) {
      console.error('[AdminProjects] loadLineItems failed:', err);
    } finally {
      setLineItemsLoading(false);
    }
  };

  const handleSaveRate = async (lineItemId) => {
    const newRate = parseFloat(editRateValue);
    if (isNaN(newRate) || newRate < 0) return;
    const ok = await updateLineItemRate(selectedProjectId, lineItemId, newRate);
    if (ok) {
      setLineItems(prev => prev.map(li => {
        if (li.line_item_id === lineItemId) {
          const vRate = parseFloat(li.vexus_rate) || 0;
          return { ...li, contractor_rate: newRate, margin: vRate - newRate };
        }
        return li;
      }));
    }
    setEditingRate(null);
    setEditRateValue('');
  };

  const exportLineItemsCSV = () => {
    if (!lineItems.length) return;
    const headers = ['Code', 'Description', 'UOM', 'Qty', 'Vexus Rate', 'Contractor Rate', 'Margin', 'Vexus Total', 'Contractor Total'];
    const rows = lineItems.map(li => {
      const qty = parseFloat(li.quantity) || 0;
      const vRate = parseFloat(li.vexus_rate) || 0;
      const cRate = parseFloat(li.contractor_rate) || 0;
      return [li.code, li.description, li.uom, qty, vRate, cRate, (vRate - cRate).toFixed(2), (qty * vRate).toFixed(2), (qty * cRate).toFixed(2)];
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedProjectId}_line_items.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadCOs = useCallback(async () => {
    if (!selectedProjectId) return;
    setCosLoading(true);
    try {
      const cos = await loadChangeOrders(selectedProjectId);
      setChangeOrders(cos);
    } catch (err) {
      console.error('[AdminProjects] loadChangeOrders failed:', err);
    } finally {
      setCosLoading(false);
    }
  }, [selectedProjectId]);

  const handleCreateCO = async (coData) => {
    await createChangeOrder(coData);
    await loadCOs();
  };

  const handleApproveCO = async (coId) => {
    await updateChangeOrderField(coId, 'status', 'Approved');
    await loadCOs();
  };

  const handleRejectCO = async (coId) => {
    await updateChangeOrderField(coId, 'status', 'Rejected');
    await loadCOs();
  };

  const ProgressBar = ({ percent, color, label }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.75rem', color: textMuted }}>{label}</span>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color }}>{percent}%</span>
      </div>
      <div style={{ height: 6, backgroundColor: darkMode ? '#0d1b2a' : '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${percent}%`, backgroundColor: color, borderRadius: 3, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <Loader size={32} color={accent} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ color: textMuted }}>Loading project data...</span>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }}
      style={{ minHeight: '100vh', backgroundColor: bg, color: text }}>
      {/* Header */}
      <div style={{ backgroundColor: darkMode ? '#112240' : '#f1f5f9', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${borderColor}`, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button onClick={() => setCurrentPage && setCurrentPage('admin-dashboard')} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}>
            <ArrowLeft size={20} /> Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: '1.4rem' }}><span style={{ color: accent }}>Project</span> Management</h1>
          {dataSource === 'demo' && <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.65rem', fontWeight: 600, backgroundColor: '#FFB80022', color: '#FFB800', border: '1px solid #FFB80044' }}>DEMO</span>}
          {dataSource === 'live' && <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.65rem', fontWeight: 600, backgroundColor: '#4CAF5022', color: '#4CAF50', border: '1px solid #4CAF5044' }}>LIVE</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setCurrentPage && setCurrentPage('job-import')} style={{ backgroundColor: accent, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 600 }}>
            <Plus size={16} /> Import Project
          </button>
          <button onClick={() => setDarkMode && setDarkMode(!darkMode)} aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            {darkMode ? <Sun size={18} color="#FFB800" /> : <Moon size={18} color="#64748b" />}
          </button>
        </div>
      </div>

      {/* No Projects / Error State */}
      {allProjects.length === 0 && !loading && (
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          <AlertCircle size={48} color={warningOrange} style={{ marginBottom: 12 }} />
          <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: text }}>
            {dataSource === 'error' ? 'Connection Error' : 'No Projects Found'}
          </h2>
          <p style={{ color: textMuted, margin: '0 0 16px', fontSize: '0.9rem' }}>
            {dataSource === 'error'
              ? 'Could not connect to the database. Check your internet connection or try again.'
              : 'No projects have been imported yet. Use "Import Project" above to get started.'}
          </p>
          <button onClick={() => { setLoading(true); setDataSource('loading'); window.location.reload(); }}
            style={{ backgroundColor: accent, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      )}

      {/* Project Selector */}
      {allProjects.length > 0 && (
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', color: textMuted, fontWeight: 500 }}>Project:</span>
          <div style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
            <select value={selectedProjectId || ''} onChange={(e) => handleProjectChange(e.target.value)}
              style={{ width: '100%', padding: '10px 36px 10px 14px', borderRadius: 8, border: `1px solid ${borderColor}`, backgroundColor: cardBg, color: text, fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', appearance: 'none' }}>
              {allProjects.map(p => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_id} — {p.name || p.project_name || p.client || p.customer || 'Unnamed'}
                </option>
              ))}
            </select>
            <ChevronDown size={16} color={textMuted} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
          <button onClick={() => fetchProjectData(selectedProjectId)} style={{ background: 'none', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: textMuted, display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <span style={{ fontSize: '0.75rem', color: textMuted }}>{allProjects.length} project{allProjects.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Quick Stats */}
      <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Segments', value: stats.totalSegments, icon: <Map size={18} />, color: accent },
          { label: 'Total Footage', value: `${stats.totalFootage.toLocaleString()} LF`, icon: <BarChart3 size={18} />, color: '#2196F3' },
          { label: 'Splice Points', value: stats.totalSplices, icon: <Zap size={18} />, color: '#4CAF50' },
          { label: 'Active Crews', value: stats.crews, icon: <Users size={18} />, color: '#FFB800' },
          { label: 'Open Issues', value: openIssues.length, icon: <AlertTriangle size={18} />, color: openIssues.length > 0 ? warningOrange : '#4CAF50' },
          { label: 'Billed', value: `${stats.billedPct}%`, icon: <DollarSign size={18} />, color: stats.billedPct > 80 ? '#4CAF50' : '#FFB800' },
          { label: 'Paid', value: `${stats.paidPct}%`, icon: <DollarSign size={18} />, color: stats.paidPct > 80 ? '#4CAF50' : '#2196F3' },
        ].map(stat => (
          <div key={stat.label} style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ color: stat.color }}>{stat.icon}</div>
            <div>
              <div style={{ fontSize: '0.7rem', color: textMuted, marginBottom: 2 }}>{stat.label}</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ padding: '0 24px 20px', borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { icon: <Upload size={18} />, label: 'Import Work Order', page: 'job-import', color: accent },
            { icon: <Map size={18} />, label: 'Interactive Map', page: 'project-map', color: '#4CAF50' },
            { icon: <BarChart3 size={18} />, label: 'Reports', page: 'metrics', color: '#2196F3' },
            { icon: <DollarSign size={18} />, label: 'P&L Dashboard', page: 'pl-dashboard', color: '#4CAF50' },
            { icon: <DollarSign size={18} />, label: 'Client Invoice', page: 'client-invoice', color: '#2196F3' },
            { icon: <UserPlus size={18} />, label: 'Assign Crews', page: 'project-map', color: '#FFB800' },
          ].map(action => (
            <button key={action.label} onClick={action.page ? () => setCurrentPage && setCurrentPage(action.page) : action.action}
              style={{
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 10, padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                gap: 10, color: text, fontSize: '0.9rem', fontWeight: 500, flex: 1, minWidth: 160, transition: 'border-color 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = action.color}
              onMouseOut={(e) => e.currentTarget.style.borderColor = borderColor}
            >
              <span style={{ color: action.color }}>{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {/* View Tabs */}
      <div style={{ padding: '0 24px', borderBottom: `1px solid ${borderColor}`, display: 'flex', gap: 0 }}>
        {[
          { id: 'overview', label: 'Overview', icon: <Map size={16} /> },
          { id: 'billing', label: 'Billing / Line Items', icon: <DollarSign size={16} /> },
        ].map(tab => (
          <button key={tab.id} onClick={() => { setActiveView(tab.id); if (tab.id === 'billing' && lineItems.length === 0) loadBillingData(); }}
            style={{
              padding: '12px 20px', border: 'none', cursor: 'pointer',
              backgroundColor: 'transparent', color: activeView === tab.id ? accent : textMuted,
              borderBottom: activeView === tab.id ? `2px solid ${accent}` : '2px solid transparent',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', fontWeight: activeView === tab.id ? 600 : 400,
              transition: 'all 0.2s',
            }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: 24 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: '1.1rem' }}>{project?.project_name || 'Project Overview'}</h2>

        {activeView === 'billing' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Line Items — Billing Detail</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={loadBillingData}
                  style={{ background: 'none', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', color: textMuted, display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
                  <RefreshCw size={14} /> Refresh
                </button>
                <button onClick={exportLineItemsCSV}
                  disabled={lineItems.length === 0}
                  style={{ background: 'none', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '6px 14px', cursor: lineItems.length ? 'pointer' : 'not-allowed', color: lineItems.length ? accent : textMuted, display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
                  <Download size={14} /> Export CSV
                </button>
              </div>
            </div>

            {lineItemsLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: textMuted }}>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: 8 }}>Loading line items...</div>
              </div>
            ) : lineItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: textMuted, backgroundColor: cardBg, borderRadius: 12 }}>
                <DollarSign size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
                <div>No line items for this project</div>
                <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Import a project via JSON Import to populate billing data</div>
              </div>
            ) : (
              <>
                {/* Billing summary */}
                {(() => {
                  const totals = lineItems.reduce((acc, li) => {
                    const qty = parseFloat(li.quantity) || 0;
                    const vRate = parseFloat(li.vexus_rate) || 0;
                    const cRate = parseFloat(li.contractor_rate) || 0;
                    acc.vexus += qty * vRate;
                    acc.contractor += qty * cRate;
                    return acc;
                  }, { vexus: 0, contractor: 0 });
                  totals.margin = totals.vexus - totals.contractor;
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                      <div style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: '0.7rem', color: textMuted }}>Vexus Total</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#28a745' }}>${totals.vexus.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      </div>
                      <div style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: '0.7rem', color: textMuted }}>Contractor Cost</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>${totals.contractor.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      </div>
                      <div style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: '0.7rem', color: textMuted }}>Margin</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: accent }}>${totals.margin.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      </div>
                      <div style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: '0.7rem', color: textMuted }}>Line Items</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{lineItems.length}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Line items table */}
                <div style={{ border: `1px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 800 }}>
                      <thead>
                        <tr style={{ backgroundColor: darkMode ? '#0d1b2a' : '#f1f5f9' }}>
                          {['Code', 'Description', 'UOM', 'Qty', 'Vexus Rate', 'Contractor Rate', 'Margin', 'Total (V)', ''].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: h === '' ? 'center' : 'left', color: textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map(li => {
                          const qty = parseFloat(li.quantity) || 0;
                          const vRate = parseFloat(li.vexus_rate) || 0;
                          const cRate = parseFloat(li.contractor_rate) || 0;
                          const margin = vRate - cRate;
                          const total = qty * vRate;
                          const isEditing = editingRate === li.line_item_id;
                          return (
                            <tr key={li.line_item_id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600, color: accent }}>{li.code}</td>
                              <td style={{ padding: '8px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{li.description}</td>
                              <td style={{ padding: '8px 12px', color: textMuted }}>{li.uom}</td>
                              <td style={{ padding: '8px 12px', fontWeight: 600 }}>{qty.toLocaleString()}</td>
                              <td style={{ padding: '8px 12px' }}>${vRate.toFixed(2)}</td>
                              <td style={{ padding: '8px 12px' }}>
                                {isEditing ? (
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <input value={editRateValue} onChange={(e) => setEditRateValue(e.target.value)}
                                      type="number" step="0.01" min="0"
                                      style={{ width: 70, padding: '4px 6px', borderRadius: 4, border: `1px solid ${accent}`, backgroundColor: darkMode ? '#0d1b2a' : '#fff', color: text, fontSize: '0.8rem' }}
                                      autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRate(li.line_item_id); if (e.key === 'Escape') { setEditingRate(null); setEditRateValue(''); } }}
                                    />
                                    <button onClick={() => handleSaveRate(li.line_item_id)} style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 6px', cursor: 'pointer', fontSize: '0.7rem' }}>Save</button>
                                  </div>
                                ) : (
                                  <span>${cRate.toFixed(2)}</span>
                                )}
                              </td>
                              <td style={{ padding: '8px 12px', color: margin > 0 ? '#28a745' : margin < 0 ? '#e85a4f' : textMuted, fontWeight: 500 }}>
                                ${margin.toFixed(2)}
                              </td>
                              <td style={{ padding: '8px 12px', fontWeight: 600 }}>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                {!isEditing && (
                                  <button onClick={() => { setEditingRate(li.line_item_id); setEditRateValue(String(cRate)); }}
                                    title="Adjust contractor rate"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
                                    <Edit3 size={14} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeView === 'overview' && project && (
          <div style={{ backgroundColor: cardBg, borderRadius: 12, padding: 20, marginBottom: 16, border: `1px solid ${borderColor}`, cursor: 'pointer', transition: 'border-color 0.2s' }}
            onClick={() => { if (setSelectedProjectId) setSelectedProjectId(selectedProjectId); setCurrentPage && setCurrentPage('project-map'); }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = accent}
            onMouseOut={(e) => e.currentTarget.style.borderColor = borderColor}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{project.project_name || 'Unnamed Project'}</h3>
                  <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#4CAF5022', color: '#4CAF50', border: '1px solid #4CAF5044' }}>{project.status || 'Active'}</span>
                </div>
                <p style={{ margin: 0, color: textMuted, fontSize: '0.85rem' }}>
                  {project.customer || 'Unknown'} &bull; {project.project_id} &bull; PO #{project.po_number || '---'}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '0 0 2px', fontSize: '1.2rem', fontWeight: 700, color: accent }}>${(parseFloat(project.total_value) || 0).toLocaleString()}</p>
                <p style={{ margin: 0, color: textMuted, fontSize: '0.8rem' }}>{stats.totalSegments} segments &bull; {stats.crews} crews</p>
              </div>
            </div>
            <ProgressBar percent={stats.boring} color="#FFB800" label="Boring" />
            <ProgressBar percent={stats.pulling} color="#2196F3" label="Pulling" />
            <ProgressBar percent={stats.splicing} color="#4CAF50" label="Splicing" />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={(e) => { e.stopPropagation(); setCurrentPage && setCurrentPage('project-map'); }}
                style={{ backgroundColor: `${accent}15`, color: accent, border: `1px solid ${accent}33`, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', fontWeight: 500 }}>
                <Map size={14} /> View Map
              </button>
              {(stats.unassignedBoring > 0 || stats.unassignedPulling > 0) && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: warningOrange, fontSize: '0.8rem' }}>
                  <UserPlus size={14} /> {stats.unassignedBoring} boring / {stats.unassignedPulling} pulling unassigned
                </span>
              )}
              {openIssues.length > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: warningOrange, fontSize: '0.8rem', marginLeft: 'auto' }}>
                  <AlertCircle size={14} /> {openIssues.length} issues
                </span>
              )}
              <ChevronRight size={16} color={textMuted} style={{ marginLeft: 'auto' }} />
            </div>
          </div>
        )}

        {/* Section Breakdown */}
        {activeView === 'overview' && stats.sections.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>Section Breakdown</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              {stats.sections.map(section => {
                const segs = segments.filter(s => s.section === section);
                const bDone = segs.filter(s => ['QC Approved', 'Complete'].includes(s.boring_status)).length;
                const pDone = segs.filter(s => ['QC Approved', 'Complete'].includes(s.pulling_status)).length;
                const ft = segs.reduce((sum, s) => sum + (parseInt(s.footage) || 0), 0);
                const hasIssue = segs.some(s => s.boring_status === 'Issue');
                const unassigned = segs.filter(s => !s.boring_assigned_to).length;
                return (
                  <div key={section} style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 16, cursor: 'pointer' }}
                    onClick={() => setCurrentPage && setCurrentPage('project-map')}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: '1rem', fontWeight: 700, color: accent }}>Section {section}</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {hasIssue && <AlertTriangle size={14} color={warningOrange} />}
                        {unassigned > 0 && <UserPlus size={14} color={textMuted} />}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: textMuted, marginBottom: 8 }}>{segs.length} segments &bull; {ft.toLocaleString()} LF</div>
                    <div style={{ display: 'flex', gap: 8, fontSize: '0.75rem' }}>
                      <span style={{ color: '#FFB800' }}>Bore: {bDone}/{segs.length}</span>
                      <span style={{ color: '#2196F3' }}>Pull: {pDone}/{segs.length}</span>
                    </div>
                    {unassigned > 0 && <div style={{ fontSize: '0.7rem', color: textMuted, marginTop: 4 }}>{unassigned} unassigned</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Change Orders */}
        {activeView === 'overview' && (
          <div style={{ marginTop: 24 }}>
            <ChangeOrderManager
              projectId={selectedProjectId}
              changeOrders={changeOrders}
              darkMode={darkMode}
              user={user}
              onCreateCO={handleCreateCO}
              onApproveCO={handleApproveCO}
              onRejectCO={handleRejectCO}
            />
            {changeOrders.length === 0 && !cosLoading && (
              <button onClick={loadCOs} style={{ fontSize: 11, color: textMuted, background: 'none', border: 'none', cursor: 'pointer', marginTop: 4 }}>
                Load change orders
              </button>
            )}
          </div>
        )}

        {/* Open Issues */}
        {activeView === 'overview' && openIssues.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', color: warningOrange }}>
              <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Open Issues ({openIssues.length})
            </h3>
            {openIssues.map((issue, idx) => (
              <div key={idx} style={{ backgroundColor: cardBg, border: '1px solid #FF980044', borderRadius: 10, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <AlertCircle size={16} color={warningOrange} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{issue.segment_id}</div>
                  <div style={{ fontSize: '0.8rem', color: textMuted }}>{issue.description || issue.issue_type}</div>
                </div>
                <span style={{ fontSize: '0.7rem', color: textMuted }}>{issue.reported_by}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {showVersion && <div style={{ position: 'fixed', bottom: 10, right: 10, fontSize: '0.7rem', opacity: 0.5, color: darkMode ? '#fff' : '#333' }}>AdminProjectDashboard v3.0.0</div>}
    </div>
  );
}

export default AdminProjectDashboard;
